import { resolveEquippedNft, sameNft, equipmentPayload } from "@/lib/nftIdentity.js";
// @/plugins/2.pinia/modules/nfts/inventoryStore.js
import { defineStore } from "pinia";
import { ref, computed, reactive } from "vue";
import { fetchUnstakedNfts } from "./unstakedNftsStore";
import { NftService } from "./services/nftService";
import { NFT_TYPES, formatNft } from "./types/nftTypes";
import { unequipMechNft, unequipOrbNft, unequipPilotNft } from "@/api/nfts";
import { logFirebaseEvent } from "@/plugins/firebase";
import sparkleImage from "@images/sparkle.png";

export const useInventoryStore = defineStore("loadout", () => {
  const sparkleAddress =
    import.meta.env.VITE_CONTRACT_SPARKLE?.toLowerCase() || "";

  // State - match original structure
  const equipped = reactive({
    pilot: null,
    mech: null,
    orb: null,
    equipment: null,
  });

  const equipmentEditingAllowed = ref(true);
  const setEquipmentEditingAllowed = value => { equipmentEditingAllowed.value = !!value; };
  const nfts = ref([]);
  const userId = ref(null);
  let sessionVersion = 0;
  let profileVersion = 0;
  let lastEquippedProfile = null;

  // Match original loading structure
  const loading = reactive({
    equipping: false,
  });

  const isLoading = ref(false);
  const error = ref(null);

  // Active equipped type state
  const activeEquippedType = ref(NFT_TYPES.PILOT);

  // Store references
  const unstakedStore = computed(() => fetchUnstakedNfts());

  const normalizeSparkleNft = (nft) => {
    const tokenAddress = nft?.token_address?.toLowerCase() || "";
    if (!sparkleAddress || tokenAddress !== sparkleAddress) return nft;

    const defaultMetadata = {
      attributes: [],
      description: "",
      image_url: sparkleImage,
      is_anoint: false,
      level: "",
      mystic: "",
      name: "Sparkle",
      second_mystic: "",
      state: "",
      thumbnail_url: "",
    };

    return {
      ...nft,
      nftType: NFT_TYPES.SPARKLE,
      metadata: {
        ...defaultMetadata,
        ...(nft.metadata || {}),
      },
    };
  };

  // Combined and formatted NFTs - UNSTAKED ONLY
  const formattedNfts = computed(() => {
    const result = [];

    // Add unstaked NFTs ONLY
    const unstakedNfts = unstakedStore.value.unstakedNftsArray || [];
    unstakedNfts.forEach((nft) => {
      // Unstaked NFTs are already in the right format, just need to be formatted
      const sparkleNormalized = normalizeSparkleNft(nft);
      const formatted = formatNft({
        ...sparkleNormalized,
        isStaked: false,
        isUnstaked: true,
      });
      result.push(formatted);
    });

    return result;
  });

  // Mintable NFTs (canMint === true)
  const formattedMintableNfts = computed(() => {
    const result = [];
    const mintableNfts = unstakedStore.value.mintableNfts || [];

    mintableNfts.forEach((nft) => {
      const sparkleNormalized = normalizeSparkleNft(nft);
      const formatted = formatNft({
        ...sparkleNormalized,
        isStaked: false,
        isUnstaked: true,
      });
      result.push(formatted);
    });

    return result;
  });

  // Update nfts ref when formatted data changes
  const updateNfts = () => {
    nfts.value = formattedNfts.value;
  };

  // Group NFTs by type for reusability
  const categorizeByType = (collection = []) => {
    const items = Array.isArray(collection) ? collection : [];

    const pilots = items.filter((nft) => nft.type === "Pilot");
    const mechs = items.filter((nft) => nft.type === "Mech");
    const orbs = items.filter((nft) => nft.type === "Orb");
    const equipment = items.filter((nft) => nft.type === "Equipment");
    const sparkles = items.filter((nft) => nft.type === "Sparkle");

    return { pilots, mechs, orbs, equipment, sparkles };
  };

  const requestUnityUserInfoUpdate = () => {
    if (typeof window === "undefined") {
      return;
    }

    window.dispatchEvent(new Event("GetUserInfoRequest"));
    window.dispatchEvent(new Event("GetUserNFTRequest"));
  };

  // Renamed to match original API - processedNftData
  const processedNftData = computed(() => categorizeByType(nfts.value));

  // Mintable NFTs grouped by type for marketplace
  const marketplaceNfts = computed(() =>
    categorizeByType(formattedMintableNfts.value),
  );

  // Alias for new code
  const categorizedNfts = computed(() => processedNftData.value);

  // Individual default getters (to match original API)
  const defaultPilot = computed(() => {
    const unstaked = processedNftData.value.pilots.find((p) => !p.isStaked);
    return unstaked || processedNftData.value.pilots[0] || null;
  });

  const defaultMech = computed(() => {
    const unstaked = processedNftData.value.mechs.find((m) => !m.isStaked);
    return unstaked || processedNftData.value.mechs[0] || null;
  });

  const defaultOrb = computed(() => {
    const unstaked = processedNftData.value.orbs.find((o) => !o.isStaked);
    return unstaked || processedNftData.value.orbs[0] || null;
  });

  const defaultEquipment = computed(() => {
    const unstaked = processedNftData.value.equipment.find((e) => !e.isStaked);
    return unstaked || processedNftData.value.equipment[0] || null;
  });

  // Consolidated defaults object for new code
  const defaultItems = computed(() => ({
    pilot: defaultPilot.value,
    mech: defaultMech.value,
    orb: defaultOrb.value,
    equipment: defaultEquipment.value,
  }));

  // Equipped items getters
  const equippedItems = computed(() =>
    Object.values(equipped).filter((item) => item !== null),
  );
  const equippedPilot = computed(() => equipped.pilot);
  const equippedMech = computed(() => equipped.mech);
  const equippedOrb = computed(() => equipped.orb);
  const equippedEquipment = computed(() => equipped.equipment);

  // Status getters
  const isPilotEquipped = computed(() => !!equipped.pilot);
  const isMechEquipped = computed(() => !!equipped.mech);
  const isOrbEquipped = computed(() => !!equipped.orb);
  const isEquipmentEquipped = computed(() => !!equipped.equipment);

  // Active equipped type getters
  const isPilotActive = computed(
    () => activeEquippedType.value === NFT_TYPES.PILOT,
  );
  const isMechActive = computed(
    () => activeEquippedType.value === NFT_TYPES.MECH,
  );
  const isOrbActive = computed(
    () => activeEquippedType.value === NFT_TYPES.ORB,
  );
  const isEquipmentActive = computed(
    () => activeEquippedType.value === NFT_TYPES.EQUIPMENT,
  );

  // Get the currently equipped item for the active type
  const activeEquippedItem = computed(() => {
    return equipped[activeEquippedType.value];
  });

  // Get all available items for the active type
  const availableItemsForActiveType = computed(() => {
    switch (activeEquippedType.value) {
      case NFT_TYPES.PILOT:
        return availablePilots.value;
      case NFT_TYPES.MECH:
        return availableMechs.value;
      case NFT_TYPES.ORB:
        return availableOrbs.value;
      case NFT_TYPES.EQUIPMENT:
        return availableEquipment.value;
      default:
        return [];
    }
  });

  // Check if loadout is complete
  const isLoadoutComplete = computed(() => {
    const unstaked = nfts.value.filter((nft) => !nft.isStaked);
    const pilotComplete =
      !unstaked.some((nft) => nft.type === "Pilot") || !!equipped.pilot;
    const mechComplete =
      !unstaked.some((nft) => nft.type === "Mech") || !!equipped.mech;
    const orbComplete =
      !unstaked.some((nft) => nft.type === "Orb") || !!equipped.orb;
    const equipmentComplete =
      !unstaked.some((nft) => nft.type === "Equipment") || !!equipped.equipment;

    return pilotComplete && mechComplete && orbComplete && equipmentComplete;
  });

  // Available items getters (only unstaked items can be equipped)
  const availablePilots = computed(() =>
    categorizedNfts.value.pilots.filter((p) => !p.isStaked),
  );
  const availableMechs = computed(() =>
    categorizedNfts.value.mechs.filter((m) => !m.isStaked),
  );
  const availableOrbs = computed(() =>
    categorizedNfts.value.orbs.filter((o) => !o.isStaked),
  );
  const availableEquipment = computed(() =>
    categorizedNfts.value.equipment.filter((e) => !e.isStaked),
  );
  const availableSparkles = computed(() =>
    categorizedNfts.value.sparkles.filter((s) => !s.isStaked),
  );

  const mapItemsToGrid = (items, equippedItem = null) => {
    return items.map((item) => ({
      id: item.id,
      name: item.name,
      level: parseInt(item.level) || 0,
      type: item.type,
      mystic: item.mystic,
      second_mystic: item.second_mystic,
      effect: item.effect || "",
      description: item.description || "",
      equipped: sameNft(equippedItem, item),
      attributes: item.attributes || [],
      // Include these fields for API calls
      token_id: item.token_id,
      token_address: item.token_address,
      nftType: item.nftType,
      isStaked: item.isStaked || false,
      itemData: item,
    }));
  };

  // Grid-formatted inventory data
  const inventoryForGrid = computed(() => {
    return {
      pilots: mapItemsToGrid(availablePilots.value, equipped.pilot),
      mechs: mapItemsToGrid(availableMechs.value, equipped.mech),
      orbs: mapItemsToGrid(availableOrbs.value, equipped.orb),
      equipment: mapItemsToGrid(availableEquipment.value, equipped.equipment),
      sparkles: mapItemsToGrid(availableSparkles.value),
    };
  });

  // Grid data for marketplace (only mintable NFTs)
  const marketplaceMintableNfts = computed(() => {
    const mintable = marketplaceNfts.value;

    return {
      pilots: mapItemsToGrid(mintable.pilots),
      mechs: mapItemsToGrid(mintable.mechs),
      orbs: mapItemsToGrid(mintable.orbs),
      equipment: mapItemsToGrid(mintable.equipment),
    };
  });

  // Check if an item is a default item (first unstaked of its type)
  const isDefaultItem = (item) => {
    if (!item) return false;

    const type = item.type.toLowerCase();
    const defaultItem = defaultItems.value[type];

    return item.id === (defaultItem?.id || "");
  };

  // Actions
  const fetchInventory = async (forceRefresh = false) => {
    const session = sessionVersion;
    console.log("Fetching inventory from NFT stores...");
    isLoading.value = true;
    error.value = null;

    try {
      // Fetch unstaked NFTs ONLY
      if (forceRefresh || unstakedStore.value.error || unstakedStore.value.unstakedNftsArray.length === 0) {
        await unstakedStore.value.fetchUnstakedNfts(
          1,
          100,
          Boolean(forceRefresh),
        );
      }

      if (session !== sessionVersion) return false;
      if (unstakedStore.value.error) throw new Error(unstakedStore.value.error);

      // Update the nfts array with formatted data
      updateNfts();

      return true;
    } catch (err) {
      if (session !== sessionVersion) return false;
      console.error("Failed to fetch NFTs:", err);
      error.value = "Failed to load NFT inventory";
      return false;
    } finally {
      if (session === sessionVersion) isLoading.value = false;
    }
  };

  // Initialize the store
  const initialize = async () => {
    const session = sessionVersion;
    isLoading.value = true;

    try {
      // Get user ID
      const nextUserId = await NftService.fetchUserId();
      if (session !== sessionVersion) return false;
      userId.value = nextUserId;

      // Fetch inventory
      if (!await fetchInventory() || session !== sessionVersion) return false;

      // Fetch equipped items
      isLoading.value = true;
      if (!await fetchAndSetEquippedItems() || session !== sessionVersion) return false;

      // Apply defaults for empty slots
      ensureDefaultsFallback();

      return true;
    } catch (err) {
      if (session !== sessionVersion) return false;
      console.error("Failed to initialize inventory:", err);
      error.value = err.message || "Failed to initialize inventory";
      return false;
    } finally {
      if (session === sessionVersion) isLoading.value = false;
    }
  };

  // Fetch and set equipped items
  const fetchAndSetEquippedItems = async (fromMutation = false) => {
    if (loading.equipping && !fromMutation) return false;
    const session = sessionVersion;
    const request = ++profileVersion;
    const isCurrent = () => session === sessionVersion && request === profileVersion;
    try {
      const equippedItems = await NftService.fetchEquippedItems({ isCurrent });
      if (!isCurrent()) return false;

      if (!equippedItems) {
        throw new Error("No equipped items returned");
      }

      for (const [slot, group, key] of [
        ['pilot', 'pilots', 'mystic'], ['mech', 'mechs', 'mech'],
        ['orb', 'orbs', 'orb'], ['equipment', 'equipment', 'equipment'],
      ]) {
        equipped[slot] = resolveEquippedNft(
          categorizedNfts.value[group], equippedItems[key], equippedItems[`${key}Id`],
        );
      }
      lastEquippedProfile = equippedItems;
      error.value = null;
      return true;
    } catch (err) {
      if (!isCurrent()) return false;
      console.error("Failed to fetch or set equipped items:", err);
      error.value = "Failed to load equipped items";
      return false;
    }
  };

  // Ensure defaults are applied for empty slots
  const ensureDefaultsFallback = () => {};

  // Serialize mutations and publish only a current, confirmed backend profile.
  const changeEquipment = async (slot, item, chain, removing = false) => {
    if (!equipmentEditingAllowed.value) { error.value = "Dock at a portal to change your loadout."; return false; }
    if (loading.equipping) return false;
    if (!removing && unstakedStore.value.error) {
      error.value = "Refresh your NFT inventory before equipping items.";
      return false;
    }
    const session = sessionVersion;
    const isCurrent = () => session === sessionVersion;
    let payload;
    try {
      if (!removing && item?.isStaked) throw new Error("Cannot equip a staked item");
      payload = equipmentPayload(item, chain, slot === 'pilot' ? undefined : slot);
    } catch (err) {
      error.value = err.message;
      return false;
    }
    profileVersion++; // Invalidate any profile read started before this mutation.
    loading.equipping = true;
    error.value = null;
    try {
      if (removing) {
        await ({pilot: unequipPilotNft, mech: unequipMechNft, orb: unequipOrbNft})[slot](payload);
      } else {
        const method = {pilot: 'equipPilot', mech: 'equipMech', orb: 'equipOrb'}[slot];
        await NftService[method](item, payload.chain);
      }
      if (!isCurrent()) return false;
      if (!await fetchAndSetEquippedItems(true) || !isCurrent()) return false;
      const key = slot === 'pilot' ? 'mystic' : slot;
      const confirmed = removing
        ? !lastEquippedProfile?.[key] && lastEquippedProfile?.[`${key}Id`] == null
        : sameNft(equipped[slot], item);
      if (!confirmed) {
        error.value = "Equipment change is not yet confirmed. Refresh your loadout to check.";
        return false;
      }
      if (!removing) logFirebaseEvent(`equip_${slot}`, {
        id: item.id, name: item.name, chain: payload.chain,
        token_address: payload.tokenAddress, token_id: payload.tokenId,
      });
      requestUnityUserInfoUpdate();
      return true;
    } catch (err) {
      if (isCurrent()) error.value = err.response?.data?.message || err.message || "Failed to update equipment";
      return false;
    } finally {
      if (isCurrent()) loading.equipping = false;
    }
  };
  const equipPilot = (item, chain) => changeEquipment('pilot', item, chain);
  const equipMech = (item, chain) => changeEquipment('mech', item, chain);
  const equipOrb = (item, chain) => changeEquipment('orb', item, chain);
  const unequipPilot = chain => changeEquipment('pilot', equipped.pilot, chain, true);
  const unequipMech = chain => changeEquipment('mech', equipped.mech, chain, true);
  const unequipOrb = chain => changeEquipment('orb', equipped.orb, chain, true);

  // Reset loadout to defaults
  const resetLoadout = () => {
    equipped.pilot = availablePilots.value.length ? defaultPilot.value : null;
    equipped.mech = availableMechs.value.length ? defaultMech.value : null;
    equipped.orb = availableOrbs.value.length ? defaultOrb.value : null;
    equipped.equipment = availableEquipment.value.length
      ? defaultEquipment.value
      : null;
  };

  // Filter items by attribute
  const filterByAttribute = (type, traitType, value) => {
    const itemsByType =
      type.toLowerCase() === NFT_TYPES.PILOT
        ? availablePilots.value
        : type.toLowerCase() === NFT_TYPES.MECH
          ? availableMechs.value
          : type.toLowerCase() === NFT_TYPES.ORB
            ? availableOrbs.value
            : availableEquipment.value;

    return itemsByType.filter((item) => {
      return item.attributes.some(
        (attr) => attr.trait_type === traitType && attr.value === value,
      );
    });
  };

  // Active type management actions
  const setActiveEquippedType = (type) => {
    const lowerType = type.toLowerCase();

    if (!Object.values(NFT_TYPES).includes(lowerType)) {
      console.error(`Invalid equipment type: ${type}`);
      return false;
    }

    activeEquippedType.value = lowerType;
    return true;
  };

  const cycleActiveEquippedType = () => {
    const types = Object.values(NFT_TYPES);
    const currentIndex = types.indexOf(activeEquippedType.value);
    const nextIndex = (currentIndex + 1) % types.length;
    activeEquippedType.value = types[nextIndex];
    return activeEquippedType.value;
  };

  // Helper function to infer type from traits
  const inferTypeFromTraits = (nft) => {
    if (!nft || !nft.traits) {
      return "mystic"; // Default type
    }

    // Try to find a trait that indicates the type
    const typeTrait = nft.traits.find(
      (t) =>
        t.trait_type?.toLowerCase() === "type" ||
        t.trait_type?.toLowerCase() === "nft type" ||
        t.trait_type?.toLowerCase() === "species",
    );

    if (typeTrait) {
      const value = typeTrait.value?.toLowerCase();
      if (
        value.includes("pilot") ||
        value.includes("mystic") ||
        value.includes("hero")
      ) {
        return "mystic";
      } else if (value.includes("mech")) {
        return "mech";
      } else if (value.includes("orb")) {
        return "orb";
      } else if (value.includes("equipment")) {
        return "equipment";
      }
    }

    // If no type trait found, try to infer from name
    if (nft.name) {
      const name = nft.name.toLowerCase();
      if (
        name.includes("pilot") ||
        name.includes("mystic") ||
        name.includes("hero")
      ) {
        return "mystic";
      } else if (name.includes("mech")) {
        return "mech";
      } else if (name.includes("orb")) {
        return "orb";
      } else if (name.includes("equipment")) {
        return "equipment";
      }
    }

    // Default to mystic if we couldn't determine the type
    return "mystic";
  };

  // Reset all state (used on logout/account switch)
  const reset = () => {
    sessionVersion++;
    profileVersion++;
    lastEquippedProfile = null;
    equipped.pilot = null;
    equipped.mech = null;
    equipped.orb = null;
    equipped.equipment = null;
    nfts.value = [];
    userId.value = null;
    loading.equipping = false;
    isLoading.value = false;
    error.value = null;
    activeEquippedType.value = NFT_TYPES.PILOT;
    unstakedStore.value?.reset();
  };

  // Legacy method kept for backward compatibility
  const findTraitValue = (nft, traitName) => {
    if (!nft || !nft.traits || !Array.isArray(nft.traits)) {
      return null;
    }

    const trait = nft.traits.find(
      (t) => t.trait_type?.toLowerCase() === traitName.toLowerCase(),
    );

    return trait ? trait.value : null;
  };

  return {
    // State
    equipmentEditingAllowed,
    setEquipmentEditingAllowed,
    equipped,
    nfts,
    userId,
    isLoading,
    loading,
    error,
    activeEquippedType,

    // Getters
    processedNftData,
    categorizedNfts,
    marketplaceNfts,
    defaultItems,
    defaultPilot,
    defaultMech,
    defaultOrb,
    defaultEquipment,
    equippedItems,
    equippedPilot,
    equippedMech,
    equippedOrb,
    equippedEquipment,
    isPilotEquipped,
    isMechEquipped,
    isOrbEquipped,
    isEquipmentEquipped,
    isLoadoutComplete,
    availablePilots,
    availableMechs,
    availableOrbs,
    availableEquipment,
    availableSparkles,
    inventoryForGrid,
    marketplaceMintableNfts,

    // Active type getters
    isPilotActive,
    isMechActive,
    isOrbActive,
    isEquipmentActive,
    activeEquippedItem,
    availableItemsForActiveType,

    // Actions
    fetchInventory,
    initialize,
    fetchAndSetEquippedItems,
    ensureDefaultsFallback,
    equipPilot,
    equipMech,
    equipOrb,
    unequipPilot,
    unequipMech,
    unequipOrb,
    resetLoadout,
    filterByAttribute,
    isDefaultItem,

    // Active type actions
    setActiveEquippedType,
    cycleActiveEquippedType,
    reset,

    // Legacy methods kept for compatibility
    findTraitValue,
    inferTypeFromTraits,
  };
});
